/**
 * Swiper Slider Custom Element
 * A wrapper around Swiper.js for easy use in Shopify sections
 */

class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    // Wait for Swiper to be available
    if (typeof window.Swiper !== 'undefined') {
      this.init();
    } else {
      // Wait for Swiper to load
      const checkSwiper = setInterval(() => {
        if (typeof window.Swiper !== 'undefined') {
          clearInterval(checkSwiper);
          this.init();
        }
      }, 50);

      // Timeout after 5 seconds
      setTimeout(() => clearInterval(checkSwiper), 5000);
    }

    // Re-initialize on section load (Shopify editor)
    document.addEventListener('shopify:section:load', (event) => {
      if (this.closest(`[data-section-id="${event.detail.sectionId}"]`)) {
        this.destroy();
        this.init();
      }
    });
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    if (this.initialized || !window.Swiper) return;

    this.slider = new window.Swiper(this, {
      speed: this.speed,
      autoplay: this.autoplay,
      navigation: this.navigation,
      pagination: this.pagination,
      scrollbar: this.scrollbar,
      breakpoints: this.breakpoints,
      rewind: true,
      watchSlidesProgress: true,
      mousewheel: {
        enabled: true,
        forceToAxis: true
      }
    });

    this.initialized = true;
  }

  destroy() {
    if (this.slider && typeof this.slider.destroy === 'function') {
      this.slider.destroy(true, true);
      this.slider = null;
      this.initialized = false;
    }
  }

  get speed() {
    return Number(this.dataset.sliderSpeed) || 300;
  }

  get autoplay() {
    const delay = Number(this.dataset.sliderAutoplay) || 0;
    return delay === 0 ? false : { delay: delay * 1000, disableOnInteraction: false };
  }

  get navigation() {
    return {
      enabled: this.dataset.sliderNavigation === 'true',
      prevEl: this.querySelector('.swiper-button-prev'),
      nextEl: this.querySelector('.swiper-button-next')
    };
  }

  get pagination() {
    return {
      enabled: this.dataset.sliderPagination === 'true',
      el: this.querySelector('.swiper-pagination'),
      type: this.dataset.sliderPaginationType || 'bullets',
      dynamicBullets: true,
      dynamicMainBullets: 2,
      clickable: true,
      renderFraction: function(currentClass, totalClass) {
        return `<span class="${currentClass}"></span> / <span class="${totalClass}"></span>`;
      }
    };
  }

  get scrollbar() {
    return {
      enabled: this.dataset.sliderScrollbar === 'true',
      el: this.querySelector('.swiper-scrollbar'),
      draggable: true
    };
  }

  get partialSlidesMobile() {
    return this.dataset.sliderPartialSlidesMobile === 'true';
  }

  get breakpoints() {
    const partial = this.partialSlidesMobile ? 1.2 : 1;
    return {
      0: {
        slidesPerView: Number(this.dataset.breakpointXs || 1) === 1 ? partial : Number(this.dataset.breakpointXs || 1),
        spaceBetween: 0
      },
      500: {
        slidesPerView: Number(this.dataset.breakpointSm || 2),
        spaceBetween: 0
      },
      750: {
        slidesPerView: Number(this.dataset.breakpointMd || 3),
        spaceBetween: 0
      },
      990: {
        slidesPerView: Number(this.dataset.breakpointLg || 4),
        spaceBetween: 0
      },
      1400: {
        slidesPerView: Number(this.dataset.breakpointXl || 5),
        spaceBetween: 0
      }
    };
  }
}

if (!customElements.get('swiper-slider')) {
  customElements.define('swiper-slider', SwiperSlider);
}
